import dayjs from 'dayjs'
import { detailedDiff } from 'deep-object-diff'
import { forEach, has, isEqual, isObject, transform } from 'lodash'
import moment from 'moment'

import { isEmpty } from '@emmacare/common/utils/isEmpty'

import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  DEFAULT_PAGINATION,
  DEFAULT_SORT,
  FROM,
  TO
} from 'src/constants'
import { checkIsEmptyArray } from 'src/utils/Array'

export const getObjectKeyByValue = (object, value) =>
  Object.keys(object).find((key) => object[key] === value)

export const sortByNameAsc = (a, b, sortBy) => {
  if (a[sortBy] > b[sortBy]) return 1
  if (a[sortBy] < b[sortBy]) return -1
  return 0
}

export const getObjectsDiff = (objectOne, objectTwo) =>
  transform(objectOne, (result, value, key) => {
    if (!isEqual(value, objectTwo[key])) {
      result[key] =
        isObject(value) && isObject(objectTwo[key])
          ? getObjectsDiff(value, objectTwo[key])
          : value
    }
  })

export const convertObjListToArray = (obj) => {
  const languageList = []

  forEach(obj, (item, key) => {
    languageList.push({
      title: key,
      key,
      value: item
    })
  })
  return languageList
}

export const convertFilter = (filter = {}, checkDateTime = false) => {
  const updFilter = {}
  const dateFormat = checkDateTime ? DATE_TIME_FORMAT : DATE_FORMAT

  Object.keys(filter).forEach((field) => {
    updFilter[field] =
      moment.isMoment(filter[field]) || dayjs.isDayjs(filter[field])
        ? filter[field].format(dateFormat)
        : has(filter[field], FROM) && has(filter[field], TO)
        ? {
            [FROM]:
              moment.isMoment(filter[field][FROM]) ||
              dayjs.isDayjs(filter[field][FROM])
                ? filter[field][FROM].format(dateFormat)
                : null,
            [TO]:
              moment.isMoment(filter[field][TO]) ||
              dayjs.isDayjs(filter[field][TO])
                ? filter[field][TO].format(dateFormat)
                : null
          }
        : filter[field]
  })

  return updFilter
}

// TODO check default values as those are null sometimes
const compareFilterArrays = (initial = [], current = []) => {
  const initialArray = initial || []
  const currentArray = current || []
  if (
    initialArray?.length !== currentArray?.length ||
    !initialArray?.every((item) => currentArray?.includes(item)) ||
    !currentArray?.every((item) => initialArray?.includes(item))
  ) {
    return false
  }

  return true
}

export const isEqualFilters = (
  initialValues = {},
  currentValues = {},
  checkDateTime = false
) => {
  const updInitialValues = convertFilter(initialValues, checkDateTime)
  const updCurrentValues = convertFilter(currentValues, checkDateTime)
  const diff = detailedDiff(updInitialValues, updCurrentValues)

  let isEqualFilters = true

  forEach(diff, (obj = '', action = '') => {
    if (!isEqualFilters || isEmpty(obj)) return

    if (action === 'updated') {
      forEach(obj, (_, filterName = '') => {
        const checkInitialValue = initialValues[filterName]
        const checkCurrentValue = currentValues[filterName]
        // TODO: added case for checkboxes
        const isEmptyValues =
          typeof checkInitialValue === 'boolean'
            ? false
            : isEmpty(checkInitialValue) && isEmpty(checkCurrentValue)

        if (checkInitialValue instanceof Array) {
          if (
            isEqualFilters &&
            !compareFilterArrays(checkInitialValue, checkCurrentValue)
          ) {
            isEqualFilters = false
          }
        } else if (isEqualFilters && !isEmptyValues) {
          isEqualFilters = false
        }
      })
    } else {
      isEqualFilters = false
    }
  })

  return isEqualFilters
}

export const deepCheckEqual = (oldProps = null, newProps = null) => {
  if ((oldProps && !newProps) || (!oldProps && newProps)) return false

  const diff = detailedDiff(oldProps, newProps)
  const isEqual =
    Object.keys(diff.added).length === 0 &&
    Object.keys(diff.deleted).length === 0 &&
    Object.keys(diff.updated).length === 0

  return isEqual
}

export const checkValueFitsForFilter = (value = null) => {
  if (
    typeof value !== 'boolean' &&
    ((typeof value !== 'number' && !value) ||
      (Array.isArray(value) && checkIsEmptyArray(value)))
  ) {
    return false
  }

  return true
}

export const getDefaultQuery = (sorter = {}, pagination = {}) => {
  const sortedColumn = sorter?.sortedColumn || DEFAULT_SORT?.sortedColumn
  const sortedColumnOrder =
    sorter?.sortedColumnOrder || DEFAULT_SORT?.sortedColumnOrder
  const query = {
    sort: `${sortedColumn}:${sortedColumnOrder === 'ascend' ? 'asc' : 'desc'}`,
    page: pagination?.current || DEFAULT_PAGINATION?.current,
    'per-page': pagination?.pageSize || DEFAULT_PAGINATION?.pageSize
  }

  return query
}

export const formatInterventions = (
  interventions = [],
  existedInterventionId = ''
) => {
  const grouped = Object.values(
    interventions.reduce((groups, intervention) => {
      const { patientGoal } = intervention
      const patientGoalUUID = patientGoal.uuid

      if (!groups[patientGoalUUID]) {
        groups[patientGoalUUID] = {
          key: patientGoalUUID,
          value: patientGoalUUID,
          title: patientGoal.content,
          disabled: true,
          children: []
        }
      }

      if (
        intervention.status === 1 ||
        intervention.status === 2 ||
        intervention.uuid === existedInterventionId
      ) {
        groups[patientGoalUUID].children.push({
          key: intervention.uuid,
          value: intervention.uuid,
          title: intervention.content,
          disabled:
            intervention.uuid === existedInterventionId &&
            intervention.status !== 1 &&
            intervention.status !== 2
        })
      }

      return groups
    }, {})
  )

  const filteredGoals = grouped.filter((value) => !isEmpty(value.children))

  return filteredGoals
}
